import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Plus, Search, ChevronLeft, ChevronRight, Edit, EyeOff,
  Image, Play, Download, FileSpreadsheet, FileText, X,
  CheckSquare, Square, ChevronsUpDown, ChevronUp, ChevronDown,
  Clapperboard, ImagePlus, Video, Copy
} from "lucide-react";
import { copyQuestionInfo, downloadQuestionJson } from "../utils/questionHelpers";
import { SimulationButton } from "../components/QuestionSimulationModal";

const DIFFICULTY_COLORS = {
  easy: "bg-green-50 text-green-700 border-green-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  hard: "bg-red-50 text-red-700 border-red-200"
};
const DIFFICULTY_LABELS = { easy: "Kolay", medium: "Orta", hard: "Zor" };
const STATUS_COLORS = {
  active: "bg-green-50 text-green-700 border-green-200",
  passive: "bg-slate-100 text-slate-500 border-slate-200",
  draft: "bg-amber-50 text-amber-700 border-amber-200"
};
const STATUS_LABELS = { active: "Aktif", passive: "Pasif", draft: "Taslak" };
const DISPLAY_TYPE_COLORS = {
  T: "bg-blue-50 text-blue-700 border-blue-200",
  R: "bg-purple-50 text-purple-700 border-purple-200",
  TR: "bg-cyan-50 text-cyan-700 border-cyan-200",
  ZTR: "bg-pink-50 text-pink-700 border-pink-200",
  V: "bg-orange-50 text-orange-700 border-orange-200"
};

const SORTABLE = ["question_code", "lesson_name", "topic", "difficulty_level", "status", "updated_at"];
const STORAGE_KEY = "ql_state_v1";
const DEFAULT_SORT = { field: "question_code", dir: "asc" };

const loadSavedState = () => {
  try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null"); } catch { return null; }
};

function SortIcon({ field, sort }) {
  if (sort.field !== field) return <ChevronsUpDown size={11} className="text-slate-400 inline ml-0.5 opacity-50" />;
  return sort.dir === "asc"
    ? <ChevronUp size={11} className="text-blue-500 inline ml-0.5" />
    : <ChevronDown size={11} className="text-blue-500 inline ml-0.5" />;
}

// Thumbnail with lazy load, 1 retry on ERR, placeholder on final failure
function ThumbImage({ src, alt = "", className = "" }) {
  const [state, setState] = useState("loading"); // loading | ok | retry | error
  const retried = useRef(false);

  const handleLoad = () => setState("ok");
  const handleError = () => {
    if (!retried.current) {
      retried.current = true;
      setState("retry");
      // Wait 1.5s then retry by toggling back to loading
      setTimeout(() => setState("loading"), 1500);
    } else {
      setState("error");
    }
  };

  if (state === "error") {
    return (
      <span className={`flex items-center justify-center bg-slate-100 rounded-lg border border-slate-200 ${className}`}
        style={{ width: 80, height: 80 }}>
        <Image size={20} className="text-slate-300" />
      </span>
    );
  }

  return (
    <span className="relative block" style={{ width: 80, height: 80 }}>
      {/* Skeleton shown while loading or retrying */}
      {state !== "ok" && (
        <span className={`absolute inset-0 rounded-lg ${state === "retry" ? "bg-amber-50" : "bg-slate-100 animate-pulse"}`} />
      )}
      <img
        key={state === "retry" ? "retry" : "first"}
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        fetchpriority="low"
        onLoad={handleLoad}
        onError={handleError}
        className={`rounded-lg object-contain border border-slate-200 transition-opacity duration-200 ${state === "ok" ? "opacity-100" : "opacity-0"} ${className}`}
        style={{ width: 80, height: 80 }}
      />
    </span>
  );
}

function MediaCell({ q, onPreview, rowIndex = 0 }) {
  const optCount = ["a", "b", "c", "d"].filter(o => q[`option_${o}_image_url`]).length;
  const hasMedia = q.question_image_url || q.question_video_url || optCount > 0 || q.question_gif_url;
  if (!hasMedia) return <span className="text-slate-200 text-xs select-none">—</span>;

  // Stagger image load requests to avoid R2 burst (50ms per row)
  const staggerDelay = Math.min(rowIndex * 40, 600);

  return (
    <button
      onClick={(e) => { e.stopPropagation(); onPreview(q); }}
      data-testid={`media-cell-${q.question_code}`}
      className="group relative flex items-center justify-center"
      title="Medyayı önizle"
      style={{ contentVisibility: "auto" }}
    >
      {q.question_image_url ? (
        <span style={{ transitionDelay: `${staggerDelay}ms` }} className="group-hover:ring-2 group-hover:ring-blue-400 rounded-lg transition-all">
          <ThumbImage
            src={q.question_image_url}
            className="group-hover:ring-2 group-hover:ring-blue-400"
          />
        </span>
      ) : q.question_video_url ? (
        <span className="flex items-center gap-1.5 px-3 py-2 bg-orange-100 text-orange-600 rounded-lg text-xs font-semibold group-hover:bg-orange-200 transition-colors">
          <Clapperboard size={14} /> Video
        </span>
      ) : optCount > 0 ? (
        <span className="flex items-center gap-1.5 px-3 py-2 bg-cyan-50 text-cyan-600 rounded-lg text-xs font-semibold group-hover:bg-cyan-100 transition-colors border border-cyan-200">
          <Image size={14} /> {optCount} şık
        </span>
      ) : null}
    </button>
  );
}

function MediaPreviewModal({ q, onClose }) {
  if (!q) return null;
  const optImages = ["a", "b", "c", "d"].filter(o => q[`option_${o}_image_url`]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose} data-testid="media-preview-modal">
      <div className="bg-white rounded-2xl shadow-2xl p-5 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-semibold text-sm text-slate-800">{q.question_code}</p>
            <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{q.question_text}</p>
          </div>
          <button onClick={onClose} data-testid="media-preview-close"
            className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition-colors ml-4 shrink-0">
            <X size={16} />
          </button>
        </div>

        {q.question_image_url && (
          <div className="mb-3">
            <p className="text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Soru Görseli</p>
            <img src={q.question_image_url} alt="Soru görseli"
              className="w-full rounded-xl border border-slate-200 object-contain max-h-72"
              onError={(e) => { e.target.style.display = "none"; }} />
          </div>
        )}

        {q.question_video_url && (
          <div className="mb-3">
            <p className="text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">Soru Videosu</p>
            <video src={q.question_video_url} controls muted playsInline preload="metadata"
              className="w-full rounded-xl border border-slate-200 max-h-56 bg-black" />
          </div>
        )}

        {q.question_gif_url && (
          <div className="mb-3">
            <p className="text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">GIF</p>
            <img src={q.question_gif_url} alt="GIF"
              className="w-full rounded-xl border border-slate-200 object-contain max-h-48" />
          </div>
        )}

        {optImages.length > 0 && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">Şık Görselleri</p>
            <div className="grid grid-cols-2 gap-2">
              {optImages.map(opt => (
                <div key={opt} className="space-y-1">
                  <div className="flex items-baseline gap-1.5 bg-slate-50 rounded px-2 py-1">
                    <span className="text-xs font-bold text-blue-600 shrink-0">{opt.toUpperCase()}</span>
                    {q[`option_${opt}`] && (
                      <span className="text-xs text-slate-500 truncate" title={q[`option_${opt}`]}>
                        {q[`option_${opt}`]}
                      </span>
                    )}
                  </div>
                  <img src={q[`option_${opt}_image_url`]} alt={`Şık ${opt.toUpperCase()}`}
                    className="rounded-lg border border-slate-200 object-contain w-full max-h-36"
                    onError={(e) => { e.target.style.display = "none"; }} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Display type groups — persisted in localStorage
const TYPE_GROUPS = [
  { key: "metin",  label: "Metin",  types: ["T"],           color: "text-blue-700 border-blue-300 bg-blue-50"   },
  { key: "gorsel", label: "Görsel", types: ["R","TR","ZTR"], color: "text-purple-700 border-purple-300 bg-purple-50" },
  { key: "video",  label: "Video",  types: ["V"],           color: "text-orange-700 border-orange-300 bg-orange-50" },
];
const LS_KEY = "qbank_type_filter";
function loadTypeGroups() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return new Set(parsed);
    }
  } catch {}
  return new Set();
}

// ── Quick media upload for list rows ─────────────────────────────────────────
function QuickMediaUploadBtn({ questionId, questionCode, fieldKey, accept, icon: Icon, title, onSuccess }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleLabelClick = async (e) => {
    e.stopPropagation();
    const num = questionCode?.match(/\d+/)?.[0];
    if (num) {
      try {
        await navigator.clipboard.writeText(num);
        toast.info(`"${num}" kopyalandı`);
      } catch { /* clipboard permission denied */ }
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const uploadRes = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = uploadRes.data.url || uploadRes.data.storage_path;
      await api.patch(`/questions/${questionId}/quick-fix`, { field: fieldKey, value: url });
      toast.success(`${title} yüklendi`);
      onSuccess?.(questionId, fieldKey, url);
    } catch { toast.error("Yükleme başarısız"); }
    finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <label className="cursor-pointer" title={title} onClick={handleLabelClick}>
      <span className={`flex items-center justify-center p-1.5 rounded transition-colors ${
        uploading ? "text-blue-500" : "text-slate-300 hover:text-blue-500 hover:bg-blue-50"
      }`}>
        {uploading
          ? <div className="w-3 h-3 border border-blue-400 border-t-transparent rounded-full animate-spin" />
          : <Icon size={13} />}
      </span>
      <input ref={inputRef} type="file" accept={accept} className="hidden"
        onChange={(e) => handleUpload(e.target.files[0])} />
    </label>
  );
}

// ── Reusable Pagination ───────────────────────────────────────────────────────
function buildPages(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, 2, total - 1, total]);
  for (let i = current - 1; i <= current + 1; i++) {
    if (i > 0 && i <= total) pages.add(i);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) result.push("…");
    result.push(p);
    prev = p;
  }
  return result;
}

function Pagination({ page, pages, total, onChange, compact = false }) {
  if (pages <= 1) return null;
  const items = buildPages(page, pages);
  return (
    <div className={`flex items-center gap-1 ${compact ? "" : "justify-between"}`} data-testid="pagination">
      {!compact && (
        <p className="text-xs text-slate-500 shrink-0">
          Sayfa <strong>{page}</strong> / {pages} — <strong>{total}</strong> soru
        </p>
      )}
      <div className="flex items-center gap-0.5">
        {/* Önceki */}
        <button
          onClick={() => onChange(Math.max(1, page - 1))}
          disabled={page === 1}
          data-testid="prev-page-btn"
          className="flex items-center justify-center h-7 w-7 rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft size={13} />
        </button>

        {/* Sayfa numaraları */}
        {items.map((item, i) =>
          item === "…" ? (
            <span key={`ellipsis-${i}`} className="flex items-center justify-center h-7 w-6 text-slate-400 text-xs select-none">…</span>
          ) : (
            <button
              key={item}
              onClick={() => onChange(item)}
              data-testid={`page-btn-${item}`}
              className={`flex items-center justify-center h-7 min-w-[28px] px-1.5 rounded border text-xs font-medium transition-colors ${
                item === page
                  ? "bg-blue-600 border-blue-600 text-white shadow-sm"
                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {item}
            </button>
          )
        )}

        {/* Sonraki */}
        <button
          onClick={() => onChange(Math.min(pages, page + 1))}
          disabled={page === pages}
          data-testid="next-page-btn"
          className="flex items-center justify-center h-7 w-7 rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight size={13} />
        </button>
      </div>
    </div>
  );
}

export default function QuestionList() {
  const [searchParams] = useSearchParams();
  const initFilters = {
    search: searchParams.get("search") || "",
    archive_name: searchParams.get("archive_name") || "",
    lesson_name: searchParams.get("lesson_name") || "",
    topic: searchParams.get("topic") || "",
    subtopic: searchParams.get("subtopic") || "",
    difficulty_level: searchParams.get("difficulty_level") || "",
    status: searchParams.get("status") || "active",
    unit_name: "",
    taxonomy_level_1: "",
  };
  const initMissingData = searchParams.get("missing_data") === "true";

  // Load saved filter state (restored when navigating back from question detail)
  const savedState = useMemo(() => loadSavedState(), []);

  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(() => savedState?.page || 1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => savedState?.filters || initFilters);
  const [selectedTypeGroups, setSelectedTypeGroups] = useState(loadTypeGroups);
  const [missingData] = useState(initMissingData);
  const [filterOptions, setFilterOptions] = useState({
    archive_names: [], lesson_names: [], topics: [], subtopics: [], unit_names: [], taxonomy_level_1: [],
  });

  // Cascade options
  const [cascadeTopics, setCascadeTopics] = useState([]);
  const [cascadeSubtopics, setCascadeSubtopics] = useState([]);
  const [cascadeUnits, setCascadeUnits] = useState([]);
  const [cascadeTax1, setCascadeTax1] = useState([]);

  // Track initial mount so cascade effects don't reset children when restoring saved state
  const isInitialMount = useRef(true);
  useEffect(() => { isInitialMount.current = false; }, []);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFmt, setExportFmt] = useState("xlsx");
  const [exportScope, setExportScope] = useState("all");
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef(null);
  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  // Sort — default: question_code asc
  const [sort, setSort] = useState(() => savedState?.sort || DEFAULT_SORT);
  // Media preview
  const [previewQ, setPreviewQ] = useState(null);
  const navigate = useNavigate();

  // Persist filters/page/sort to sessionStorage on every change
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ filters, page, sort }));
  }, [filters, page, sort]);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    setBulkConfirm(null);
    try {
      const params = { page, limit: 20 };
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v !== null) params[k] = v;
      });
      // Display type multi-select
      if (selectedTypeGroups.size > 0) {
        const types = [...selectedTypeGroups].flatMap(
          g => TYPE_GROUPS.find(t => t.key === g)?.types || []
        );
        if (types.length > 0) params.display_type_in = types.join(",");
      }
      if (sort.field) { params.sort_by = sort.field; params.sort_order = sort.dir; }
      if (missingData) params.missing_data = true;
      const res = await api.get("/questions", { params });
      setQuestions(res.data.questions);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch { toast.error("Sorular yüklenemedi"); }
    finally { setLoading(false); }
  }, [page, filters, sort, selectedTypeGroups]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  useEffect(() => {
    api.get("/questions/meta/filters")
      .then((r) => setFilterOptions({
        archive_names: r.data.archive_names || [],
        lesson_names: r.data.lesson_names || [],
        topics: r.data.topics || [],
        subtopics: r.data.subtopics || [],
        unit_names: r.data.unit_names || [],
        taxonomy_level_1: r.data.taxonomy_level_1 || [],
      }))
      .catch(() => {});
  }, []);

  const setFilter = (key, val) => {
    setPage(1);
    setFilters((f) => ({ ...f, [key]: val }));
  };

  const clearFilters = () => {
    const clean = { search: "", archive_name: "", lesson_name: "", topic: "", subtopic: "", difficulty_level: "", status: "active", unit_name: "", taxonomy_level_1: "" };
    setFilters(clean);
    setSelectedTypeGroups(new Set());
    setSort(DEFAULT_SORT);
    setPage(1);
    setCascadeTopics([]); setCascadeSubtopics([]); setCascadeUnits([]); setCascadeTax1([]);
    sessionStorage.removeItem(STORAGE_KEY);
  };

  // Cascade: lesson → topics
  useEffect(() => {
    if (filters.lesson_name) {
      api.get("/taxonomy/children", { params: { parent_type: "lesson_name", parent_value: filters.lesson_name, child_type: "topic" } })
        .then((r) => setCascadeTopics(r.data || []))
        .catch(() => setCascadeTopics([]));
      if (!isInitialMount.current) {
        setFilters((f) => ({ ...f, topic: "", subtopic: "", unit_name: "", taxonomy_level_1: "" }));
        setCascadeSubtopics([]); setCascadeUnits([]); setCascadeTax1([]);
      }
    } else if (!isInitialMount.current) {
      setCascadeTopics([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.lesson_name]);

  // Cascade: topic → subtopics
  useEffect(() => {
    if (filters.topic) {
      api.get("/taxonomy/children", { params: { parent_type: "topic", parent_value: filters.topic, child_type: "subtopic" } })
        .then((r) => setCascadeSubtopics(r.data || []))
        .catch(() => setCascadeSubtopics([]));
      if (!isInitialMount.current) {
        setFilters((f) => ({ ...f, subtopic: "", unit_name: "", taxonomy_level_1: "" }));
        setCascadeUnits([]); setCascadeTax1([]);
      }
    } else if (!isInitialMount.current) {
      setCascadeSubtopics([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.topic]);

  // Cascade: subtopic → units
  useEffect(() => {
    if (filters.subtopic) {
      api.get("/taxonomy/children", { params: { parent_type: "subtopic", parent_value: filters.subtopic, child_type: "unit_name" } })
        .then((r) => setCascadeUnits(r.data || []))
        .catch(() => setCascadeUnits([]));
      if (!isInitialMount.current) {
        setFilters((f) => ({ ...f, unit_name: "", taxonomy_level_1: "" }));
        setCascadeTax1([]);
      }
    } else if (!isInitialMount.current) {
      setCascadeUnits([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.subtopic]);

  // Cascade: unit → taxonomy_level_1
  useEffect(() => {
    if (filters.unit_name) {
      api.get("/taxonomy/children", { params: { parent_type: "unit_name", parent_value: filters.unit_name, child_type: "taxonomy_level_1" } })
        .then((r) => setCascadeTax1(r.data || []))
        .catch(() => setCascadeTax1([]));
      if (!isInitialMount.current) {
        setFilters((f) => ({ ...f, taxonomy_level_1: "" }));
      }
    } else if (!isInitialMount.current) {
      setCascadeTax1([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.unit_name]);

  const toggleTypeGroup = (key) => {
    setPage(1);
    setSelectedTypeGroups(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      localStorage.setItem(LS_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleSelectAll = () => {
    const pageIds = questions.map((q) => q.id);
    const allSelected = pageIds.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      pageIds.forEach((id) => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const clearSelection = () => { setSelected(new Set()); setBulkConfirm(null); };

  const handleSort = (field) => {
    setSort(prev => {
      if (prev.field !== field) return { field, dir: "asc" };
      if (prev.dir === "asc") return { field, dir: "desc" };
      return { field: null, dir: null };
    });
    setPage(1);
  };

  const handleBulkStatus = async (status) => {
    setBulkLoading(true);
    try {
      const res = await api.post("/questions/bulk-status", { ids: [...selected], status });
      const label = status === "active" ? "aktifleştirildi" : "pasife alındı";
      toast.success(`${res.data.updated} soru ${label}`);
      clearSelection();
      fetchQuestions();
    } catch { toast.error("İşlem başarısız"); }
    finally { setBulkLoading(false); }
  };

  const handleSetPassive = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Bu soruyu pasife almak istiyor musunuz?")) return;
    try {
      await api.post(`/questions/${id}/set-passive`);
      toast.success("Soru pasife alındı");
      fetchQuestions();
    } catch { toast.error("İşlem başarısız"); }
  };

  // Close export panel when clicking outside
  useEffect(() => {
    const handler = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { format: exportFmt };
      if (exportScope === "filtered") {
        Object.entries(filters).forEach(([k, v]) => {
          if (v !== "" && v !== null) {
            if (k === "has_image") params[k] = v === "true";
            else params[k] = v;
          }
        });
      }
      const res = await api.get("/questions/export", { params, responseType: "blob" });
      const ext = exportFmt === "xlsx" ? "xlsx" : "csv";
      const date = new Date().toISOString().slice(0, 10);
      const filename = `sorular_${date}.${ext}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success(`${filename} indirildi`);
      setExportOpen(false);
    } catch { toast.error("Export başarısız"); }
    finally { setExporting(false); }
  };

  return (
    <div className="p-8 fade-in" style={selected.size > 0 ? { paddingBottom: "7rem" } : {}}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Soru Listesi</h1>
          <p className="text-sm text-slate-500 mt-0.5">{total} soru bulundu</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Export button + panel */}
          <div className="relative" ref={exportRef}>
            <Button variant="outline" onClick={() => setExportOpen((o) => !o)}
              data-testid="export-btn"
              className="h-9 text-sm gap-2 border-slate-200 text-slate-600 hover:bg-slate-50">
              <Download size={14} /> Dışa Aktar
            </Button>

            {exportOpen && (
              <div className="absolute right-0 top-11 z-50 w-64 bg-white border border-slate-200 rounded-xl shadow-lg p-4 space-y-4"
                data-testid="export-panel">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-700">Dışa Aktar</p>
                  <button onClick={() => setExportOpen(false)} className="text-slate-400 hover:text-slate-600">
                    <X size={14} />
                  </button>
                </div>

                {/* Format */}
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Format</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { val: "xlsx", label: "Excel", icon: FileSpreadsheet },
                      { val: "csv",  label: "CSV",   icon: FileText }
                    ].map(({ val, label, icon: Icon }) => (
                      <button key={val} onClick={() => setExportFmt(val)}
                        data-testid={`export-fmt-${val}`}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                          exportFmt === val
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>
                        <Icon size={13} /> {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Scope */}
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Kapsam</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { val: "all",      label: "Tümü" },
                      { val: "filtered", label: "Filtrelenmiş" }
                    ].map(({ val, label }) => (
                      <button key={val} onClick={() => setExportScope(val)}
                        data-testid={`export-scope-${val}`}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                          exportScope === val
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <Button onClick={handleExport} disabled={exporting}
                  data-testid="export-confirm-btn"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm h-9 gap-2">
                  <Download size={13} />
                  {exporting ? "Hazırlanıyor..." : "İndir"}
                </Button>
              </div>
            )}
          </div>

          <Link to="/questions/new" data-testid="add-question-btn"
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors">
            <Plus size={15} /> Yeni Soru
          </Link>
        </div>
      </div>

      {/* Filters */}
      {/* ── FİLTRE BARI — 2 SATIR ── */}
      <div className="mb-4 p-3 bg-white border border-slate-200 rounded-xl shadow-sm space-y-2" data-testid="filter-bar">

        {/* Satır 1: Arama + Ana kategoriler */}
        <div className="flex flex-wrap items-center gap-2">

          {/* Arama */}
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Kod veya metin ara..." value={filters.search}
              onChange={(e) => setFilter("search", e.target.value)}
              data-testid="search-input"
              className="pl-8 h-8 text-xs border-slate-200 w-44" />
          </div>

          {/* Arşiv */}
          <select value={filters.archive_name} onChange={(e) => setFilter("archive_name", e.target.value)}
            data-testid="filter-archive_name"
            className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500">
            <option value="">Tüm Arşivler</option>
            {filterOptions.archive_names.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>

          {/* Ders */}
          <select value={filters.lesson_name} onChange={(e) => setFilter("lesson_name", e.target.value)}
            data-testid="filter-lesson_name"
            className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500">
            <option value="">Tüm Dersler</option>
            {filterOptions.lesson_names.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>

          {/* Konu — cascade from Ders */}
          <select value={filters.topic} onChange={(e) => setFilter("topic", e.target.value)}
            data-testid="filter-topic"
            className={`h-8 text-xs border rounded-md px-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 max-w-[200px] ${
              filters.lesson_name && cascadeTopics.length === 0 ? "border-amber-200 text-amber-500" : "border-slate-200 text-slate-600"
            }`}>
            <option value="">
              {filters.lesson_name && cascadeTopics.length === 0 ? "Konu Bulunamadı" : "Tüm Konular"}
            </option>
            {(cascadeTopics.length > 0 ? cascadeTopics : filterOptions.topics).map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>

          {/* Alt Konu — cascade from Konu */}
          <select value={filters.subtopic} onChange={(e) => setFilter("subtopic", e.target.value)}
            data-testid="filter-subtopic"
            className={`h-8 text-xs border rounded-md px-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 max-w-[200px] ${
              filters.topic && cascadeSubtopics.length === 0 ? "border-amber-200 text-amber-500" : "border-slate-200 text-slate-600"
            }`}>
            <option value="">
              {filters.topic && cascadeSubtopics.length === 0 ? "Alt Konu Bulunamadı" : filters.lesson_name && !filters.topic ? "Önce Konu seçin" : "Tüm Alt Konular"}
            </option>
            {(cascadeSubtopics.length > 0 ? cascadeSubtopics : (!filters.lesson_name ? filterOptions.subtopics : [])).map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>

          {/* Ünite — cascade from Alt Konu */}
          <select value={filters.unit_name} onChange={(e) => setFilter("unit_name", e.target.value)}
            data-testid="filter-unit_name"
            className={`h-8 text-xs border rounded-md px-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 max-w-[180px] ${
              filters.unit_name ? "border-blue-300 text-blue-700 font-medium" : "border-slate-200 text-slate-600"
            }`}>
            <option value="">
              {filters.subtopic && cascadeUnits.length === 0 ? "Ünite Bulunamadı" : filters.lesson_name && !filters.subtopic ? "Önce Alt Konu seçin" : "Tüm Üniteler"}
            </option>
            {(cascadeUnits.length > 0 ? cascadeUnits : (!filters.lesson_name ? filterOptions.unit_names : [])).map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>

          {/* Taksonomi 1 — cascade from Ünite */}
          <select value={filters.taxonomy_level_1} onChange={(e) => setFilter("taxonomy_level_1", e.target.value)}
            data-testid="filter-taxonomy_level_1"
            className={`h-8 text-xs border rounded-md px-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 max-w-[180px] ${
              filters.taxonomy_level_1 ? "border-blue-300 text-blue-700 font-medium" : "border-slate-200 text-slate-600"
            }`}>
            <option value="">
              {filters.unit_name && cascadeTax1.length === 0 ? "Taksonomi Bulunamadı" : "Tüm Taksonomi 1"}
            </option>
            {(cascadeTax1.length > 0 ? cascadeTax1 : filterOptions.taxonomy_level_1).map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>

          {/* Filtreleri Temizle */}
          {(Object.values(filters).some(v => v && v !== "active") || selectedTypeGroups.size > 0) && (
            <button onClick={clearFilters} data-testid="clear-filters-btn"
              className="flex items-center gap-1 h-8 px-3 text-xs font-medium text-red-500 border border-red-200 bg-red-50 hover:bg-red-100 rounded-md transition-colors whitespace-nowrap">
              <X size={12} /> Filtreleri Temizle
            </button>
          )}
        </div>

        {/* Satır 2: Durum + Zorluk + Tip checkboxları */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">

          <select value={filters.status} onChange={(e) => setFilter("status", e.target.value)}
            data-testid="filter-status"
            className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500">
            <option value="">Tüm Durumlar</option>
            <option value="active">Aktif</option>
            <option value="passive">Pasif</option>
            <option value="draft">Taslak</option>
          </select>

          <select value={filters.difficulty_level} onChange={(e) => setFilter("difficulty_level", e.target.value)}
            data-testid="filter-difficulty"
            className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500">
            <option value="">Tüm Zorluklar</option>
            <option value="easy">Kolay</option>
            <option value="medium">Orta</option>
            <option value="hard">Zor</option>
          </select>

          {/* Tip checkboxları */}
          <div className="flex items-center gap-1 border border-slate-200 rounded-md bg-white px-1.5 h-8" data-testid="filter-type-group">
            {TYPE_GROUPS.map(({ key, label, color }) => {
              const active = selectedTypeGroups.has(key);
              return (
                <button key={key} data-testid={`type-filter-${key}`} onClick={() => toggleTypeGroup(key)}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border transition-all ${
                    active ? color + " border-current" : "text-slate-400 border-transparent hover:bg-slate-50"
                  }`}>
                  <span className={`w-3 h-3 rounded-sm border flex items-center justify-center flex-shrink-0 ${
                    active ? "bg-current border-current" : "border-slate-300"
                  }`}>
                    {active && <svg viewBox="0 0 10 10" className="w-2 h-2 fill-white"><path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>}
                  </span>
                  {label}
                </button>
              );
            })}
          </div>

        </div>
      </div>

      {/* ── Üst Pagination ── */}
      {pages > 1 && (
        <div className="flex items-center justify-between mb-2 px-1" data-testid="pagination-top">
          <p className="text-xs text-slate-500">
            Sayfa <strong>{page}</strong> / {pages} — <strong>{total}</strong> soru
          </p>
          <Pagination page={page} pages={pages} total={total} onChange={setPage} compact />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="questions-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="h-10">
              <th className="w-10 px-3 align-middle text-center">
                <button onClick={toggleSelectAll} data-testid="select-all-checkbox"
                  className="text-slate-400 hover:text-blue-600 transition-colors">
                  {questions.length > 0 && questions.every(q => selected.has(q.id))
                    ? <CheckSquare size={15} className="text-blue-600" />
                    : <Square size={15} />}
                </button>
              </th>
              {[
                { key: "question_code", label: "Kod",    cls: "w-28 text-left" },
                { key: null,            label: "Tip",    cls: "w-14 text-center" },
                { key: null,            label: "Soru",   cls: "text-left" },
                { key: "lesson_name",   label: "Ders",   cls: "w-28 text-left" },
                { key: "topic",         label: "Konu",   cls: "w-28 text-left" },
                { key: null,            label: "Medya",  cls: "w-28 text-center" },
                { key: "difficulty_level", label: "Zorluk", cls: "w-24 text-left" },
                { key: "status",        label: "Durum",  cls: "w-24 text-left" },
                { key: null,            label: "İşlem",  cls: "w-24 text-right" },
              ].map(({ key, label, cls }) => (
                <th key={label} className={`px-4 align-middle text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap ${cls} ${key ? "cursor-pointer hover:text-slate-800 select-none" : ""}`}
                  onClick={key ? () => handleSort(key) : undefined}>
                  <span className="inline-flex items-center gap-0.5">
                    {label}{key && <SortIcon field={key} sort={sort} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i} className="border-b border-slate-50">
                  {[...Array(9)].map((_, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>)}
                </tr>
              ))
            ) : questions.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-sm text-slate-400">Soru bulunamadı</td>
              </tr>
            ) : questions.map((q, i) => (
              <tr key={q.id}
                className={`hover:bg-slate-50 transition-colors cursor-pointer ${selected.has(q.id) ? "bg-blue-50" : ""} ${i < questions.length - 1 ? "border-b border-slate-100" : ""}`}
                onClick={() => navigate(`/questions/${q.id}`)}
                data-testid={`question-row-${q.question_code}`}
              >
                <td className="w-10 px-3 py-2 text-center" onClick={(e) => { e.stopPropagation(); toggleSelect(q.id); }}>
                  <button data-testid={`checkbox-${q.question_code}`}
                    className="text-slate-400 hover:text-blue-600 transition-colors">
                    {selected.has(q.id)
                      ? <CheckSquare size={15} className="text-blue-600" />
                      : <Square size={15} />}
                  </button>
                </td>
                <td className="px-4 py-2">
                  <span className="font-mono text-xs text-blue-600 font-medium">{q.question_code}</span>
                </td>
                <td className="px-4 py-2 text-center">
                  {q.display_type ? (
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-bold border ${DISPLAY_TYPE_COLORS[q.display_type] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                      {q.display_type}
                    </span>
                  ) : <span className="text-slate-300 text-xs">—</span>}
                </td>
                <td className="px-4 py-2 text-slate-700 text-xs max-w-xs truncate">{q.question_text}</td>
                <td className="px-4 py-2 text-slate-500 text-xs truncate">{q.lesson_name || "—"}</td>
                <td className="px-4 py-2 text-slate-400 text-xs truncate">{q.topic || "—"}</td>
                <td className="px-4 py-2 text-center">
                  <MediaCell q={q} onPreview={setPreviewQ} rowIndex={i} />
                </td>
                <td className="px-4 py-2">
                  {q.difficulty_level && (
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium border ${DIFFICULTY_COLORS[q.difficulty_level] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                      {DIFFICULTY_LABELS[q.difficulty_level] || q.difficulty_level}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium border ${STATUS_COLORS[q.status] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                    {STATUS_LABELS[q.status] || q.status}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                    <QuickMediaUploadBtn
                      questionId={q.id}
                      questionCode={q.question_code}
                      fieldKey="question_image_url"
                      accept="image/*"
                      icon={ImagePlus}
                      title="Görsel Yükle"
                      onSuccess={(qid, field, url) => setQuestions(prev => prev.map(item => item.id === qid ? { ...item, [field]: url } : item))}
                    />
                    <QuickMediaUploadBtn
                      questionId={q.id}
                      questionCode={q.question_code}
                      fieldKey="question_video_url"
                      accept="video/*"
                      icon={Video}
                      title="Video Yükle"
                      onSuccess={(qid, field, url) => setQuestions(prev => prev.map(item => item.id === qid ? { ...item, [field]: url } : item))}
                    />
                    <SimulationButton question={q} size="sm" />
                    <button onClick={() => copyQuestionInfo(q)} data-testid={`copy-btn-${q.question_code}`}
                      className="p-1.5 text-slate-300 hover:text-violet-500 hover:bg-violet-50 rounded transition-colors" title="Bilgileri Kopyala">
                      <Copy size={13} />
                    </button>
                    <button onClick={() => downloadQuestionJson(q)} data-testid={`json-btn-${q.question_code}`}
                      className="p-1.5 text-slate-300 hover:text-emerald-500 hover:bg-emerald-50 rounded transition-colors" title="JSON İndir">
                      <Download size={13} />
                    </button>
                    {q.status === "active" && (
                      <button onClick={(e) => handleSetPassive(q.id, e)} data-testid={`passive-btn-${q.question_code}`}
                        className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors" title="Pasife Al">
                        <EyeOff size={13} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {pages > 1 && (
          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50" data-testid="pagination-bottom">
            <Pagination page={page} pages={pages} total={total} onChange={setPage} />
          </div>
        )}
      </div>

      {/* ── Media Preview Modal ── */}
      <MediaPreviewModal q={previewQ} onClose={() => setPreviewQ(null)} />

      {/* ── Floating Bulk Action Bar ── */}
      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 bg-slate-900 text-white rounded-2xl shadow-2xl border border-slate-700 text-sm"
          data-testid="bulk-action-bar">

          {bulkConfirm ? (
            /* ── Onay aşaması ── */
            <div className="flex items-center gap-3">
              <span className="text-slate-300 text-xs">
                {selected.size} soruyu <span className="font-semibold text-white">
                  {bulkConfirm === "passive" ? "pasife al" : "aktifleştir"}
                </span>?
              </span>
              <button onClick={() => handleBulkStatus(bulkConfirm)} disabled={bulkLoading}
                data-testid="bulk-confirm-yes"
                className="px-3 py-1.5 bg-white text-slate-900 rounded-lg text-xs font-semibold hover:bg-slate-100 transition-colors disabled:opacity-50">
                {bulkLoading ? "İşleniyor..." : "Evet"}
              </button>
              <button onClick={() => setBulkConfirm(null)} disabled={bulkLoading}
                data-testid="bulk-confirm-cancel"
                className="px-3 py-1.5 bg-slate-700 text-white rounded-lg text-xs font-medium hover:bg-slate-600 transition-colors">
                İptal
              </button>
            </div>
          ) : (
            /* ── Aksiyon seçimi ── */
            <>
              <span className="text-slate-400 text-xs font-medium tabular-nums">
                <span className="text-white font-bold">{selected.size}</span> soru seçildi
              </span>
              <div className="w-px h-4 bg-slate-600" />
              <button onClick={() => setBulkConfirm("active")}
                data-testid="bulk-activate-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-white rounded-lg text-xs font-semibold transition-colors">
                Aktifleştir
              </button>
              <button onClick={() => setBulkConfirm("passive")}
                data-testid="bulk-passive-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-white rounded-lg text-xs font-semibold transition-colors">
                Pasife Al
              </button>
              <button onClick={clearSelection}
                data-testid="bulk-clear-btn"
                className="p-1.5 text-slate-400 hover:text-white transition-colors rounded-lg" title="Seçimi temizle">
                <X size={14} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
